import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, ENVIRONMENT_INITIALIZER, inject } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { RegionVariantService } from 'app/core/region-variant/region-variant.service';
import { provideTransloco } from 'app/core/transloco/transloco.provider';
import { mockApiServices } from 'app/mock-api';
import { environment } from 'environments/environment';
import { provideCore, provideDefaults, provideMarkdown, provideSentry, provideValidation } from './app.providers';

const AVAILABLE_THEMES = ['theme-default', 'theme-brand', 'theme-teal', 'theme-rose', 'theme-purple', 'theme-amber', 'theme-aryza-insolv2-uk-theme'];

/** Falls back to 'theme-default' if the environment names a theme that isn't registered. */
const resolveTheme = (theme: string): string => {
  if (AVAILABLE_THEMES.includes(theme)) {
    return theme;
  }
  console.warn(`Unknown theme "${theme}" in environment config, falling back to theme-default.`);
  return 'theme-default';
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    // Keep the date adapter's locale (day/month ordering) in sync with the selected RegionVariant
    // (e.g. en-GB, en-AU) rather than the app's generic 'en' LOCALE_ID, which formats as mm/dd/yyyy.
    // IntlDateAdapter handles this and is used, but if the default Material DateAdapter is used in the future this will keep it the same.
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        const dateAdapter = inject(DateAdapter);
        const regionVariantService = inject(RegionVariantService);
        dateAdapter.setLocale(regionVariantService.getLocalisation());
        regionVariantService.changes$.subscribe(() => dateAdapter.setLocale(regionVariantService.getLocalisation()));
      },
    },

    // Transloco Config
    provideTransloco(),

    // Fuse
    provideAuth(),
    provideIcons(),
    provideFuse({
      mockApi: {
        delay: 0,
        services: mockApiServices,
      },
      fuse: {
        layout: 'classy',
        scheme: 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: resolveTheme(environment.theme),
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
          {
            id: 'theme-aryza-insolv2-uk-theme',
            name: 'Aryza Insolv2 UK',
          },
        ],
      },
    }),

    // Insol providers
    provideCore(),
    provideSentry(),
    // provideBadgeSinks(), // Not used at the moment
    provideDefaults(),
    provideMarkdown(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideValidation(),
  ],
};
